import { CalculusArea } from './CalculusAreas';
import {
  HierarchyLevels,
  PackageType,
  PriceSuggestionPriority,
  PriceSuggestionType,
} from '@retail/products/types';
import { CalculusConditionDTO } from '../dtos';
import { SupplierBase } from '@retail/suppliers/types';
import { RoundingRuleSet } from './ConditionTypes';
import { PriceRoundingBrand } from '@retail/rounding/types';

export enum PriceType {
  GTIN = 'GTIN',
  NET = 'NET',
  REBATE = 'REBATE',
}

export enum Operation {
  UPSERT = 'UPSERT',
  DELETE = 'DELETE',
}

export enum AssortmentType {
  BASE = 'BASE',
  DRAFT = 'DRAFT',
  CUSTOMER_BASE = 'CUSTOMER_BASE',
}

export interface SupplierItemBinding {
  supplierItemId: number;
  mgItemNumber: number;
  gtinCode?: string;
  packageType?: PackageType;
}

interface CalculusConditionProps {
  id: number;
  assortmentId: number;
  assortmentName: string;
  inDraft: boolean;
  supplier?: SupplierBase;
  type: CalculusArea;
  bindingType: HierarchyLevels;
  bindingTarget: string;
  bindingTargetName: string;
  bindingTargetId: string;
  bindingTargetItem?: SupplierItemBinding;
  validFrom: string;
  validTo?: string;

  purchaseAgreementText?: string;
  purchaseAgreementNumber?: string;
  purchaseAgreementId?: number;
  purchasePriceType?: PriceType;
  purchasePackageType?: PackageType;
  agreementText?: string;
  supplierAgreementId?: string;

  freightPercentAddon?: number;
  freightFixedAddOn?: number;
  freightFixedAddOnUnit?: string;

  markupCoverageDegree?: number;
  markupSalesFactor?: number;
  markupCostFactor?: number;
  coverageDegree?: number;
  salesFactor?: number;

  priceSuggestionPriorities?: PriceSuggestionPriority[];
  actualPriceSuggestionType?: PriceSuggestionType;
  priceSuggestionType?: PriceSuggestionType;

  roundingRule?: RoundingRuleSet;
}

export class CalculusCondition {
  id: number;
  assortmentId: number;
  assortmentName: string;
  inDraft: boolean;
  supplier?: SupplierBase;
  type: CalculusArea;
  bindingType: HierarchyLevels;
  bindingTarget: string;
  bindingTargetName: string;
  bindingTargetId: string;
  bindingTargetItem?: SupplierItemBinding;
  validFrom: string;
  validTo?: string;

  purchaseAgreementText?: string;
  purchaseAgreementNumber?: string;
  purchaseAgreementId?: number;
  purchasePriceType?: PriceType;
  purchasePackageType?: PackageType;
  agreementText?: string;
  supplierAgreementId?: string;

  freightPercentAddon?: number;
  freightFixedAddOn?: number;
  freightFixedAddOnUnit?: string;

  markupCoverageDegree?: number;
  markupSalesFactor?: number;
  markupCostFactor?: number;
  coverageDegree?: number;
  salesFactor?: number;

  priceSuggestionPriorities?: PriceSuggestionPriority[];
  actualPriceSuggestionType?: PriceSuggestionType;
  priceSuggestionType?: PriceSuggestionType;

  roundingRule?: RoundingRuleSet;

  constructor({
    id,
    assortmentId,
    assortmentName,
    inDraft,
    supplier,
    type,
    bindingType,
    bindingTarget,
    bindingTargetName,
    bindingTargetId,
    bindingTargetItem,
    validFrom,
    validTo,

    purchaseAgreementText,
    purchaseAgreementNumber,
    purchaseAgreementId,
    purchasePriceType,
    purchasePackageType,
    agreementText,
    supplierAgreementId,

    freightFixedAddOn,
    freightFixedAddOnUnit,
    freightPercentAddon,

    markupCoverageDegree,
    markupSalesFactor,
    markupCostFactor,
    coverageDegree,
    salesFactor,

    priceSuggestionPriorities,
    actualPriceSuggestionType,
    priceSuggestionType,

    roundingRule,
  }: CalculusConditionProps) {
    this.id = id;
    this.assortmentId = assortmentId;
    this.assortmentName = assortmentName;
    this.inDraft = inDraft;
    this.type = type;
    this.supplier = supplier;
    this.bindingType = bindingType;
    this.bindingTarget = bindingTarget;
    this.bindingTargetName = bindingTargetName;
    this.bindingTargetId = bindingTargetId;
    this.bindingTargetItem = bindingTargetItem;
    this.validFrom = validFrom;
    this.validTo = validTo;

    this.purchaseAgreementText = purchaseAgreementText;
    this.purchaseAgreementNumber = purchaseAgreementNumber;
    this.purchaseAgreementId = purchaseAgreementId;
    this.purchasePriceType = purchasePriceType;
    this.purchasePackageType = purchasePackageType;
    this.agreementText = agreementText;
    this.supplierAgreementId = supplierAgreementId;

    this.freightFixedAddOn = freightFixedAddOn;
    this.freightFixedAddOnUnit = freightFixedAddOnUnit;
    this.freightPercentAddon = freightPercentAddon;

    this.markupCoverageDegree = markupCoverageDegree;
    this.markupSalesFactor = markupSalesFactor;
    this.markupCostFactor = markupCostFactor;
    this.coverageDegree = coverageDegree;
    this.salesFactor = salesFactor;

    this.priceSuggestionPriorities = priceSuggestionPriorities;
    this.actualPriceSuggestionType = actualPriceSuggestionType;
    this.priceSuggestionType = priceSuggestionType;

    this.roundingRule = roundingRule;
  }

  static fromDto = (
    dto: CalculusConditionDTO,
    selectedBrand: PriceRoundingBrand
  ): CalculusCondition =>
    new CalculusCondition({
      id: dto.id,
      assortmentId: dto.assortmentId,
      assortmentName: dto.assortmentName,
      inDraft: dto.inDraft,
      supplier: dto.supplier ? SupplierBase.fromDto(dto.supplier) : undefined,
      type: dto.type,
      bindingType: dto.bindingType,
      bindingTarget: dto.bindingTarget,
      bindingTargetName: dto.bindingTargetName,
      bindingTargetId: dto.bindingTargetId,
      bindingTargetItem: dto.bindingTargetItem,
      validFrom: dto.validFrom,
      validTo: dto.validTo,

      purchaseAgreementText: dto.purchaseAgreementText,
      purchaseAgreementNumber: dto.purchaseAgreementNumber,
      purchaseAgreementId: dto.purchaseAgreementId,
      purchasePriceType: dto.purchasePriceType,
      purchasePackageType: dto.purchasePackageType,
      agreementText: dto.agreementText,
      supplierAgreementId: dto.supplierAgreementId,

      freightFixedAddOn: dto.freightFixedAddOn,
      freightFixedAddOnUnit: dto.freightFixedAddOnUnit,
      freightPercentAddon: dto.freightPercentAddon,

      markupCoverageDegree: dto.markupCoverageDegree,
      markupSalesFactor: dto.markupSalesFactor,
      markupCostFactor: dto.markupCostFactor,
      coverageDegree: dto.coverageDegree,
      salesFactor: dto.salesFactor,

      priceSuggestionPriorities: dto.priceSuggestionPriorities,
      actualPriceSuggestionType: dto.actualPriceSuggestionType,
      priceSuggestionType: dto.priceSuggestionType,

      roundingRule: dto.rounding ? RoundingRuleSet.fromDto(dto.rounding, selectedBrand) : undefined,
    });
}
